import {
  mergedScopedValues,
  scopedMapsByDerivingAthleteUniversalFields,
  scopedMapsForValues,
  type ScopedDynamicValueMaps,
} from "@/models/athleteUniversalFields";
import type { CountryResource } from "@/models/countryResource";
import { decodeDynamicValue, dynamicValueDisplayText, type LeagueDynamicFieldValue } from "@/models/dynamicFieldValue";
import {
  FIELD_TYPES,
  UNIVERSAL_ATHLETE_FIELD_ROLES,
  UNIVERSAL_ATHLETE_ROLE_FIELD_IDS,
  UNIVERSAL_ATHLETE_ROLE_HELP_TEXT,
  UNIVERSAL_ATHLETE_ROLE_LABELS,
  UNIVERSAL_ATHLETE_ROLE_OPTIONS,
  UNIVERSAL_ATHLETE_ROLE_SUGGESTED_LABELS,
  suggestedTypeForAthleteRole,
  type LeagueAthleteFieldDefinition,
  type LeagueAthleteFieldType,
  type LeagueAthleteUniversalFieldRole,
} from "@/models/fieldDefinition";
import {
  athleteValueAllValues,
  athleteValueID,
  type LeagueAthleteValue,
  type LeagueFieldSchema,
} from "@/models/schema";
import {
  DEFAULT_SENTINEL_VALUES,
  EXTERNAL_VALUE_TRANSFORMS,
  type ExternalFieldMapping,
  type ExternalFieldMappingProfile,
  type ExternalMappingProvenance,
  type ExternalRecordKeyStrategy,
  type ExternalValueMapEntry,
  type ExternalValueTransform,
} from "@/models/externalSource";
import { resolvedAthleteName, validateAthleteMaps } from "./athleteValues";

export type ExternalRecordContainer = "array" | "keyedObject";
export type ExternalObservedType = "string" | "integer" | "double" | "bool" | "object" | "array" | "null";
export type ExternalDetectedFormat = "isoDateTime" | "isoDate" | "url" | "booleanString" | "hexColor" | "numericString";

export interface ExternalSourceRecordBatch {
  records: Record<string, unknown>[];
  container: ExternalRecordContainer;
  suggestedRecordsPath?: string;
}

export interface ExternalSourceSchema {
  recordCount: number;
  container: ExternalRecordContainer;
  fields: ExternalSourceFieldObservation[];
}

export interface ExternalSourceFieldObservation {
  path: string;
  keyName: string;
  observedTypes: ExternalObservedType[];
  presenceCount: number;
  nullOrSentinelCount: number;
  totalRecords: number;
  sampleValues: string[];
  distinctValueCount?: number;
  enumValues: string[];
  detectedFormat?: ExternalDetectedFormat;
  duplicateConceptGroup?: string;
  mapKeyedGroup?: { normalizedPattern: string; memberKeys: string[]; primaryKey: string };
}

export type ExternalMappingDisposition = "map" | "newField" | "skip" | "unmatched";

export interface ExternalMappingProposal {
  sourcePath: string;
  disposition: ExternalMappingDisposition;
  mapping?: ExternalFieldMapping;
  proposedNewField?: LeagueAthleteFieldDefinition;
  confidence: number;
  provenance: ExternalMappingProvenance;
  reason?: string;
}

export interface ExternalMappedRecord {
  values: Record<string, LeagueDynamicFieldValue>;
  issues: { targetFieldID: string; sourcePath: string; message: string }[];
}

export interface ExternalImportSummary {
  createdCount: number;
  updatedCount: number;
  skippedCount: number;
  addedFieldIDs: string[];
  recordIssues: string[];
}

export interface PreparedExternalImport {
  schema: LeagueFieldSchema;
  schemaChanged: boolean;
  values: LeagueAthleteValue[];
  summary: ExternalImportSummary;
}

const candidateThreshold = 0.5;
const deterministicAcceptThreshold = 0.82;
const maxSampleValues = 6;
const maxSampleValueLength = 80;
const maxTrackedDistinctValues = 200;
const enumDistinctValueLimit = 12;

const roleSynonyms: Record<string, LeagueAthleteUniversalFieldRole> = {
  id: "participant-id",
  athleteid: "participant-id",
  participantid: "participant-id",
  contactid: "participant-id",
  fullname: "full-name",
  name: "full-name",
  displayname: "full-name",
  shortname: "short-name",
  firstname: "first-name",
  givenname: "first-name",
  lastname: "last-name",
  surname: "last-name",
  familyname: "last-name",
  broadcastname: "broadcast-name",
  wtcname: "broadcast-name",
  division: "division",
  cat: "competition-category",
  category: "competition-category",
  competitioncategory: "competition-category",
  country: "country-name",
  countryname: "country-name",
  repcountry: "country-name",
  nationality: "country-name",
  countrycode: "country-code",
  ioccode: "country-code",
  noc: "country-code",
  flagurl: "flag-url",
  flag: "flag-url",
  flagimage: "flag-url",
  headshot: "headshot-url",
  headshoturl: "headshot-url",
  photo: "headshot-url",
  photourl: "headshot-url",
  image: "headshot-url",
  age: "age",
  birthdate: "date-of-birth",
  dateofbirth: "date-of-birth",
  dob: "date-of-birth",
  born: "date-of-birth",
  racedtoday: "is-racing-today",
  racingtoday: "is-racing-today",
  isracingtoday: "is-racing-today",
  bib: "bib",
  athletebib: "bib",
  racenumber: "bib",
  bibnumber: "bib",
  lane: "bib",
  capcolor: "uniform-marker",
  uniformmarker: "uniform-marker",
  jerseycolor: "uniform-marker",
  helmetcolor: "uniform-marker",
  capcolorhex: "uniform-marker-hex",
  uniformmarkerhex: "uniform-marker-hex",
  notable: "is-notable",
  isnotable: "is-notable",
  notableselect: "is-notable",
  notablesort: "notable-sort-key",
  notablesortkey: "notable-sort-key",
  leaderboardselect: "leaderboard-select",
  lastupdated: "last-updated-at",
  lastupdatedat: "last-updated-at",
  lastavgupdatetimestamp: "last-updated-at",
  updatedat: "last-updated-at",
};

export function inferExternalSourceSchema(batch: ExternalSourceRecordBatch): ExternalSourceSchema {
  const sampleRecords = batch.records.slice(0, 50);
  const accumulators = new Map<string, FieldAccumulator>();
  for (const record of sampleRecords) {
    visitRecord(record, "", accumulators);
  }

  const observations = [...accumulators.entries()]
    .map(([path, accumulator]) => accumulator.observation(path, keyName(path), sampleRecords.length))
    .sort((first, second) => first.path.localeCompare(second.path));
  markDuplicateConcepts(observations);
  return {
    recordCount: batch.records.length,
    container: batch.container,
    fields: observations,
  };
}

export function deterministicProposals(
  schema: ExternalSourceSchema,
  existingFields: LeagueAthleteFieldDefinition[],
  countries: CountryResource[] = [],
): ExternalMappingProposal[] {
  const mappableFields = schema.fields.filter(isMappable);
  const claimedRoles = new Set<LeagueAthleteUniversalFieldRole>();
  const scored: Array<{ observation: ExternalSourceFieldObservation; role: LeagueAthleteUniversalFieldRole; score: number }> = [];
  for (const observation of mappableFields) {
    for (const role of UNIVERSAL_ATHLETE_FIELD_ROLES) {
      const score = matchScore(observation, role, countries);
      if (score >= candidateThreshold) scored.push({ observation, role, score });
    }
  }
  scored.sort((first, second) => second.score - first.score);

  const bestByPath = new Map<string, { role: LeagueAthleteUniversalFieldRole; score: number }>();
  for (const entry of scored) {
    if (bestByPath.has(entry.observation.path) || claimedRoles.has(entry.role)) continue;
    bestByPath.set(entry.observation.path, { role: entry.role, score: entry.score });
    claimedRoles.add(entry.role);
  }

  const existingFieldByRole = new Map(existingFields.flatMap((field) => (field.universalAthleteRole ? [[field.universalAthleteRole, field]] : [])));
  const existingFieldBySlug = new Map(existingFields.map((field) => [sbotSlug(field.id), field]));

  return mappableFields.map((observation) => {
    const match = bestByPath.get(observation.path);
    if (match) {
      return {
        sourcePath: observation.path,
        disposition: "map",
        mapping: suggestedMapping(observation, match.role, existingFieldByRole.get(match.role), match.score, "deterministic"),
        confidence: match.score,
        provenance: "deterministic",
      };
    }
    const existingField = existingFieldBySlug.get(sbotSlug(observation.keyName));
    if (existingField) {
      return {
        sourcePath: observation.path,
        disposition: "map",
        mapping: suggestedMapping(observation, existingField.universalAthleteRole, existingField, 0.9, "deterministic"),
        confidence: 0.9,
        provenance: "deterministic",
      };
    }
    return { sourcePath: observation.path, disposition: "unmatched", confidence: 0, provenance: "deterministic" };
  });
}

export function profileFromProposals(
  proposals: ExternalMappingProposal[],
  recordKeyStrategy: ExternalRecordKeyStrategy = "participant-id",
): ExternalFieldMappingProfile {
  return {
    profileVersion: 1,
    entityKind: "athletes",
    recordKeyStrategy,
    mappings: proposals.flatMap((proposal) => (proposal.mapping && proposal.disposition !== "skip" ? [proposal.mapping] : [])),
    skippedSourcePaths: proposals.filter((proposal) => proposal.disposition === "skip").map((proposal) => proposal.sourcePath),
  };
}

export function mapExternalRecord(record: Record<string, unknown>, profile: ExternalFieldMappingProfile): ExternalMappedRecord {
  const values: Record<string, LeagueDynamicFieldValue> = {};
  const issues: ExternalMappedRecord["issues"] = [];
  for (const mapping of profile.mappings) {
    const rawValue = resolvedValue(mapping, record);
    if (rawValue === undefined) continue;
    const transformed = transformedValue(rawValue, mapping);
    if (typeof transformed === "string") {
      issues.push({ targetFieldID: mapping.targetFieldID, sourcePath: mapping.sourcePath, message: transformed });
    } else if (transformed) {
      values[mapping.targetFieldID] = transformed;
    }
  }
  return { values, issues };
}

export function prepareExternalImport({
  leagueID,
  sportID,
  records,
  profile,
  acceptedNewFields,
  importAsActive,
  updatedByUserID,
  currentSchema,
  existingValues,
  countries,
  now = new Date(),
}: {
  leagueID: string;
  sportID?: string;
  records: Record<string, unknown>[];
  profile: ExternalFieldMappingProfile;
  acceptedNewFields: LeagueAthleteFieldDefinition[];
  importAsActive: boolean;
  updatedByUserID: string;
  currentSchema: LeagueFieldSchema;
  existingValues: LeagueAthleteValue[];
  countries: CountryResource[];
  now?: Date;
}): PreparedExternalImport {
  const schema: LeagueFieldSchema = {
    ...currentSchema,
    leagueID,
    sportID: currentSchema.sportID ?? sportID,
    fields: currentSchema.fields.map((field) => ({ ...field, options: [...field.options], validation: { ...field.validation } })),
  };
  const addedFieldIDs = appendMissingFields(schema, profile, acceptedNewFields);
  const activeFields = schema.fields.filter((field) => !field.isArchived).sort((first, second) => first.sortOrder - second.sortOrder);
  const fieldIDByRole = fieldIDsByRole(activeFields);
  const existingByID = new Map(existingValues.map((value) => [value.id, value]));
  const existingByParticipantID = new Map<string, LeagueAthleteValue>();
  const participantFieldID = fieldIDByRole.get("participant-id");
  if (participantFieldID) {
    for (const value of existingValues) {
      const participantID = stringValue(athleteValueAllValues(value)[participantFieldID]);
      if (participantID) existingByParticipantID.set(participantID, value);
    }
  }

  const summary: ExternalImportSummary = { createdCount: 0, updatedCount: 0, skippedCount: 0, addedFieldIDs, recordIssues: [] };
  const pendingValues = new Map<string, LeagueAthleteValue>();
  const pendingOrder: string[] = [];

  records.forEach((record, index) => {
    const mapped = mapExternalRecord(record, profile);
    const remappedValues = remapByResolvedFieldID(mapped.values, profile, fieldIDByRole);
    const routedMaps = scopedMapsByDerivingAthleteUniversalFields(scopedMapsForValues(remappedValues, activeFields), activeFields, countries, now);
    const errors = validateAthleteMaps(activeFields, routedMaps, importAsActive);
    if (Object.keys(errors).length > 0 || mapped.issues.length > 0) {
      summary.skippedCount += 1;
      const reason = Object.values(errors)[0] ?? mapped.issues[0]?.message ?? "Record could not be mapped.";
      summary.recordIssues.push(`Record ${index + 1}: ${reason}`);
      return;
    }

    const allValues = mergedScopedValues(routedMaps);
    const participantID = stringValue(allValues[fieldIDByRole.get("participant-id") ?? ""]);
    const candidate = candidateID(leagueID, profile, allValues, fieldIDByRole, participantID);
    const existing = (participantID ? existingByParticipantID.get(participantID) : undefined) ?? existingByID.get(candidate);
    const id =
      existing?.id ??
      uniqueImportID(leagueID, profile, allValues, fieldIDByRole, participantID, new Set([...existingByID.keys(), ...pendingValues.keys()]));
    const value: LeagueAthleteValue = {
      id,
      leagueID,
      name: resolvedAthleteName(undefined, routedMaps, activeFields),
      status: existing?.status === "active" ? "active" : importAsActive ? "active" : "draft",
      athleteUserID: existing?.athleteUserID,
      organizationID: existing?.organizationID,
      publicValues: routedMaps.publicValues,
      organizationValues: routedMaps.organizationValues,
      leagueValues: routedMaps.leagueValues,
      createdByUserID: existing?.createdByUserID ?? updatedByUserID,
      createdAt: existing?.createdAt ?? now,
      updatedAt: now,
      updatedByUserID,
    };

    if (!pendingValues.has(id)) pendingOrder.push(id);
    else summary.recordIssues.push(`Record ${index + 1}: overwrote an earlier record in this import with the same identifier '${id}'.`);
    pendingValues.set(id, value);
    if (existing) summary.updatedCount += 1;
    else summary.createdCount += 1;
  });

  return {
    schema: { ...schema, version: schema.version + (addedFieldIDs.length > 0 ? 1 : 0), updatedAt: now, updatedByUserID },
    schemaChanged: addedFieldIDs.length > 0,
    values: pendingOrder.flatMap((id) => pendingValues.get(id) ?? []),
    summary,
  };
}

export function makeFieldForRole(
  role: LeagueAthleteUniversalFieldRole,
  sortOrder: number,
  existingIDs: Set<string> = new Set(),
): LeagueAthleteFieldDefinition {
  const baseID = UNIVERSAL_ATHLETE_ROLE_FIELD_IDS[role];
  let id = baseID;
  let suffix = 2;
  while (existingIDs.has(id)) {
    id = `${baseID}-${suffix}`;
    suffix += 1;
  }
  const type = suggestedTypeForAthleteRole(role);
  return {
    id,
    label: UNIVERSAL_ATHLETE_ROLE_SUGGESTED_LABELS[role],
    type,
    privacyScope: "public",
    isRequired: role === "full-name" || role === "participant-id",
    helpText: UNIVERSAL_ATHLETE_ROLE_HELP_TEXT[role],
    options: UNIVERSAL_ATHLETE_ROLE_OPTIONS[role] ?? [],
    validation: { allowedMIMETypes: [] },
    sortOrder,
    isArchived: false,
    universalAthleteRole: role,
  };
}

export function newFieldFromObservation(observation: ExternalSourceFieldObservation, sortOrder: number): LeagueAthleteFieldDefinition {
  return {
    id: sbotSlug(observation.keyName),
    label: titleLabel(observation.keyName),
    type: inferredFieldType(observation),
    privacyScope: "league",
    isRequired: false,
    options: observation.enumValues.map((value) => ({ id: sbotSlug(value) || value, label: value })),
    validation: { allowedMIMETypes: [] },
    sortOrder,
    isArchived: false,
  };
}

export function inferredFieldType(observation: ExternalSourceFieldObservation): LeagueAthleteFieldType {
  switch (observation.detectedFormat) {
    case "isoDate":
      return "date";
    case "isoDateTime":
      return "dateTime";
    case "url":
      return "url";
    case "booleanString":
      return "boolean";
    case "numericString":
      return "decimal";
    case "hexColor":
      return "text";
    default:
      break;
  }
  if (observation.observedTypes.includes("bool")) return "boolean";
  if (observation.observedTypes.includes("integer")) return "integer";
  if (observation.observedTypes.includes("double")) return "decimal";
  return observation.enumValues.length > 1 ? "singleSelect" : "text";
}

export function mergeMappingProposals(deterministic: ExternalMappingProposal[], ai: ExternalMappingProposal[]): ExternalMappingProposal[] {
  const aiByPath = new Map(ai.map((proposal) => [proposal.sourcePath, proposal]));
  const usedTargetIDs = new Set<string>();
  const merged = deterministic.map((proposal) => {
    if (proposal.disposition === "map" && proposal.confidence >= deterministicAcceptThreshold) {
      if (proposal.mapping?.targetFieldID) usedTargetIDs.add(proposal.mapping.targetFieldID);
      return proposal;
    }
    const aiProposal = aiByPath.get(proposal.sourcePath);
    if (!aiProposal) return proposal;
    if (aiProposal.mapping?.targetFieldID) {
      if (usedTargetIDs.has(aiProposal.mapping.targetFieldID)) return proposal;
      usedTargetIDs.add(aiProposal.mapping.targetFieldID);
    }
    return aiProposal;
  });
  const deterministicPaths = new Set(deterministic.map((proposal) => proposal.sourcePath));
  for (const proposal of ai) {
    if (deterministicPaths.has(proposal.sourcePath)) continue;
    if (proposal.mapping?.targetFieldID && usedTargetIDs.has(proposal.mapping.targetFieldID)) continue;
    merged.push(proposal);
  }
  return merged;
}

export function parseAIProposalJSON(
  raw: string,
  schema: ExternalSourceSchema,
  existingFields: LeagueAthleteFieldDefinition[],
): ExternalMappingProposal[] {
  const parsed = JSON.parse(raw) as { mappings?: unknown };
  if (!Array.isArray(parsed.mappings)) throw new Error("AI response did not include mappings.");
  const observationsByPath = new Map(schema.fields.map((field) => [field.path, field]));
  const existingFieldsByID = new Map(existingFields.map((field) => [field.id, field]));
  const seenPaths = new Set<string>();
  const proposals: ExternalMappingProposal[] = [];

  for (const rawEntry of parsed.mappings) {
    const entry = asRecord(rawEntry);
    const sourcePath = typeof entry?.["source-path"] === "string" ? entry["source-path"].trim() : "";
    const observation = observationsByPath.get(sourcePath);
    if (!entry || !sourcePath || !observation || seenPaths.has(sourcePath)) continue;
    seenPaths.add(sourcePath);
    const action = typeof entry.action === "string" ? entry.action.toLowerCase() : "skip";
    const confidence = clamp(typeof entry.confidence === "number" ? entry.confidence : 0.5);
    const reason = typeof entry.reason === "string" ? entry.reason : undefined;
    if (action === "skip") {
      proposals.push({ sourcePath, disposition: "skip", confidence, provenance: "ai", reason });
      continue;
    }
    const role = parseRole(entry["target-universal-role"]);
    const existingFieldID = typeof entry["target-field-id"] === "string" ? entry["target-field-id"] : undefined;
    const existingField = existingFieldID ? existingFieldsByID.get(existingFieldID) : undefined;
    const shouldCreateNewField = action === "new-field" || (!role && !existingField);
    const proposedNewField = shouldCreateNewField ? parseNewField(entry["new-field"], observation, existingFields.length) : undefined;
    if (!role && !existingField && !proposedNewField) continue;
    const targetType = existingField?.type ?? (role ? suggestedTypeForAthleteRole(role) : undefined) ?? proposedNewField?.type ?? "text";
    const transforms = parseTransforms(entry.transforms);
    const targetFieldID = existingField?.id ?? (role ? UNIVERSAL_ATHLETE_ROLE_FIELD_IDS[role] : undefined) ?? proposedNewField?.id ?? sbotSlug(observation.keyName);
    const mapping: ExternalFieldMapping = {
      targetFieldID,
      targetUniversalRole: role ?? existingField?.universalAthleteRole,
      targetType,
      targetLabel: existingField?.label ?? (role ? UNIVERSAL_ATHLETE_ROLE_SUGGESTED_LABELS[role] : undefined) ?? proposedNewField?.label ?? observation.keyName,
      sourcePath,
      fallbackPaths: stringArray(entry["fallback-paths"]).filter((path) => path !== sourcePath && observationsByPath.has(path)),
      transforms: transforms.length > 0 ? transforms : suggestedTransforms(observation, targetType),
      sentinelValues: DEFAULT_SENTINEL_VALUES,
      valueMap: parseValueMap(entry["value-map"]),
      confidence,
      provenance: "ai",
    };
    proposals.push({
      sourcePath,
      disposition: proposedNewField ? "newField" : "map",
      mapping,
      proposedNewField,
      confidence,
      provenance: "ai",
      reason,
    });
  }
  if (proposals.length === 0) throw new Error("AI response included no usable mapping entries.");
  return proposals;
}

export function mappingSystemPrompt(existingFields: LeagueAthleteFieldDefinition[]): string {
  const lines = [
    "You map external sports data fields onto SBot's universal athlete schema.",
    "",
    "UNIVERSAL ROLES (id | label | type | notes):",
  ];
  for (const role of UNIVERSAL_ATHLETE_FIELD_ROLES) {
    const options = UNIVERSAL_ATHLETE_ROLE_OPTIONS[role]?.map((option) => option.id).join(", ");
    lines.push(
      `- ${role} | ${UNIVERSAL_ATHLETE_ROLE_SUGGESTED_LABELS[role]} | ${suggestedTypeForAthleteRole(role)}${
        options ? ` | options: [${options}]` : ""
      } | ${UNIVERSAL_ATHLETE_ROLE_HELP_TEXT[role]}`,
    );
  }
  const customFields = existingFields.filter((field) => !field.universalAthleteRole && !field.isArchived);
  if (customFields.length > 0) {
    lines.push("", "EXISTING CUSTOM LEAGUE FIELDS (id | label | type):");
    customFields.forEach((field) => lines.push(`- ${field.id} | ${field.label} | ${field.type}`));
  }
  lines.push(
    "",
    `ALLOWED TRANSFORM IDS: ${EXTERNAL_VALUE_TRANSFORMS.join(", ")}`,
    "",
    "Rules:",
    "- Map each source field to at most one target; use fallback-paths for duplicate source keys for the same concept.",
    "- For enum-like fields mapping to select targets, provide value-map entries from observed source values to option ids.",
    "- Propose new-field with a kebab-case id for meaningful unmatched keys; use skip for internal or noisy keys.",
    "- Prefer stable identifiers for participant-id.",
  );
  return lines.join("\n");
}

export function mappingUserMessage(schema: ExternalSourceSchema, deterministic: ExternalMappingProposal[]): string {
  const lines = [`SOURCE SCHEMA inferred from ${schema.recordCount} sample records:`];
  schema.fields
    .filter(isMappable)
    .slice(0, 80)
    .forEach((observation) => {
      const parts = [
        `path=${observation.path}`,
        `types=${observation.observedTypes.join("/")}`,
        `present=${observation.presenceCount}/${observation.totalRecords}`,
      ];
      if (observation.nullOrSentinelCount > 0) parts.push(`blank=${observation.nullOrSentinelCount}`);
      if (observation.detectedFormat) parts.push(`format=${observation.detectedFormat}`);
      if (observation.enumValues.length > 0) parts.push(`enum=[${observation.enumValues.join("|")}]`);
      else if (observation.sampleValues.length > 0) parts.push(`samples=[${observation.sampleValues.slice(0, 4).join(" | ")}]`);
      if (observation.duplicateConceptGroup) parts.push(`duplicate-of=${observation.duplicateConceptGroup}`);
      lines.push(`- ${parts.join(", ")}`);
    });
  const confident = deterministic.filter((proposal) => proposal.disposition === "map" && proposal.confidence >= deterministicAcceptThreshold);
  if (confident.length > 0) {
    lines.push("", "ALREADY AUTO-MATCHED (confirm or improve):");
    confident.forEach((proposal) => {
      if (proposal.mapping) lines.push(`- ${proposal.sourcePath} -> ${proposal.mapping.targetFieldID} (score ${proposal.confidence.toFixed(2)})`);
    });
  }
  lines.push("", "Call propose_athlete_field_mappings with a decision for every listed source path.");
  return lines.join("\n");
}

export function mappingTool() {
  return {
    name: "propose_athlete_field_mappings",
    description:
      'Propose how source data fields map onto the athlete schema. Return mappings with keys "source-path", "action", "target-universal-role", "target-field-id", "new-field", "transforms", "value-map", "fallback-paths", "confidence", and "reason".',
    parameters: {
      type: "object" as const,
      properties: {
        mappings: { type: "array" as const, description: "One mapping decision per source field path.", items: { type: "object" as const } },
      },
      required: ["mappings"],
    },
  };
}

function appendMissingFields(
  schema: LeagueFieldSchema,
  profile: ExternalFieldMappingProfile,
  acceptedNewFields: LeagueAthleteFieldDefinition[],
): string[] {
  const added: string[] = [];
  const existingIDs = new Set(schema.fields.map((field) => field.id));
  const existingRoles = new Set(schema.fields.flatMap((field) => (field.universalAthleteRole ? [field.universalAthleteRole] : [])));
  let nextSortOrder = Math.max(-1, ...schema.fields.map((field) => field.sortOrder)) + 1;
  for (const mapping of profile.mappings) {
    if (!mapping.targetUniversalRole || existingRoles.has(mapping.targetUniversalRole)) continue;
    const field = makeFieldForRole(mapping.targetUniversalRole, nextSortOrder, existingIDs);
    schema.fields.push(field);
    existingIDs.add(field.id);
    existingRoles.add(mapping.targetUniversalRole);
    added.push(field.id);
    nextSortOrder += 1;
  }
  for (const newField of acceptedNewFields) {
    const field = { ...newField, id: sbotSlug(newField.id), sortOrder: nextSortOrder };
    if (!field.id || existingIDs.has(field.id)) continue;
    schema.fields.push(field);
    existingIDs.add(field.id);
    added.push(field.id);
    nextSortOrder += 1;
  }
  return added;
}

function visitRecord(value: unknown, prefix: string, accumulators: Map<string, FieldAccumulator>) {
  const record = asRecord(value);
  if (!record) return;
  for (const [key, child] of Object.entries(record)) {
    const path = prefix ? `${prefix}.${key}` : key;
    const accumulator = accumulators.get(path) ?? new FieldAccumulator();
    accumulator.observe(child);
    accumulators.set(path, accumulator);
    if (asRecord(child)) visitRecord(child, path, accumulators);
    if (Array.isArray(child)) {
      child.slice(0, 3).forEach((entry, index) => {
        if (asRecord(entry)) visitRecord(entry, `${path}[${index}]`, accumulators);
      });
    }
  }
}

class FieldAccumulator {
  private readonly observedTypes = new Set<ExternalObservedType>();
  private presenceCount = 0;
  private nullOrSentinelCount = 0;
  private readonly sampleValues: string[] = [];
  private readonly distinctValues = new Set<string>();
  private distinctOverflowed = false;
  private classifiableValueCount = 0;
  private readonly formatVotes = new Map<ExternalDetectedFormat, number>();

  observe(value: unknown) {
    this.presenceCount += 1;
    if (value === null || value === undefined) {
      this.observedTypes.add("null");
      this.nullOrSentinelCount += 1;
      return;
    }
    if (typeof value === "boolean") {
      this.observedTypes.add("bool");
      this.trackValue(value ? "true" : "false", false);
      return;
    }
    if (typeof value === "number") {
      this.observedTypes.add(Number.isInteger(value) ? "integer" : "double");
      this.trackValue(String(value), false);
      return;
    }
    if (typeof value === "string") {
      this.observedTypes.add("string");
      if (isSentinelString(value)) {
        this.nullOrSentinelCount += 1;
        return;
      }
      this.trackValue(value, true);
      return;
    }
    this.observedTypes.add(Array.isArray(value) ? "array" : "object");
  }

  observation(path: string, keyNameValue: string, totalRecords: number): ExternalSourceFieldObservation {
    let detectedFormat: ExternalDetectedFormat | undefined;
    if (this.classifiableValueCount > 0) {
      const winner = [...this.formatVotes.entries()].sort((first, second) => second[1] - first[1])[0];
      if (winner && winner[1] / this.classifiableValueCount >= 0.8) detectedFormat = winner[0];
    }
    const distinctValueCount = this.distinctOverflowed ? undefined : this.distinctValues.size;
    const enumValues =
      distinctValueCount !== undefined &&
      distinctValueCount <= enumDistinctValueLimit &&
      (this.observedTypes.has("string") || this.observedTypes.has("integer"))
        ? [...this.distinctValues].sort()
        : [];
    return {
      path,
      keyName: keyNameValue,
      observedTypes: [...this.observedTypes].sort(),
      presenceCount: this.presenceCount,
      nullOrSentinelCount: this.nullOrSentinelCount,
      totalRecords,
      sampleValues: this.sampleValues,
      distinctValueCount,
      enumValues,
      detectedFormat,
    };
  }

  private trackValue(value: string, classify: boolean) {
    const trimmed = value.trim();
    if (!this.distinctOverflowed) {
      this.distinctValues.add(trimmed);
      if (this.distinctValues.size > maxTrackedDistinctValues) {
        this.distinctOverflowed = true;
        this.distinctValues.clear();
      }
    }
    if (this.sampleValues.length < maxSampleValues && !this.sampleValues.includes(trimmed)) {
      this.sampleValues.push(trimmed.slice(0, maxSampleValueLength));
    }
    if (!classify) return;
    this.classifiableValueCount += 1;
    const format = detectFormat(trimmed);
    if (format) this.formatVotes.set(format, (this.formatVotes.get(format) ?? 0) + 1);
  }
}

function suggestedMapping(
  observation: ExternalSourceFieldObservation,
  role: LeagueAthleteUniversalFieldRole | undefined,
  existingField: LeagueAthleteFieldDefinition | undefined,
  confidence: number,
  provenance: ExternalMappingProvenance,
): ExternalFieldMapping {
  const targetType = existingField?.type ?? (role ? suggestedTypeForAthleteRole(role) : inferredFieldType(observation));
  return {
    targetFieldID: existingField?.id ?? (role ? UNIVERSAL_ATHLETE_ROLE_FIELD_IDS[role] : sbotSlug(observation.keyName)),
    targetUniversalRole: role ?? existingField?.universalAthleteRole,
    targetType,
    targetLabel: existingField?.label ?? (role ? UNIVERSAL_ATHLETE_ROLE_SUGGESTED_LABELS[role] : titleLabel(observation.keyName)),
    sourcePath: observation.path,
    fallbackPaths: fallbackPathsFor(observation),
    transforms: suggestedTransforms(observation, targetType),
    sentinelValues: DEFAULT_SENTINEL_VALUES,
    valueMap: [],
    confidence,
    provenance,
  };
}

function suggestedTransforms(observation: ExternalSourceFieldObservation, targetType: LeagueAthleteFieldType): ExternalValueTransform[] {
  if (targetType === "dateTime" && observation.detectedFormat === "isoDateTime") return ["date-time-to-date"];
  if (targetType === "date" && observation.detectedFormat === "isoDate") return ["date-from-string"];
  if (targetType === "boolean" && observation.detectedFormat === "booleanString") return ["string-boolean-to-boolean"];
  if (targetType === "integer" && (observation.detectedFormat === "numericString" || observation.observedTypes.includes("double"))) return ["int-from-string-or-number"];
  if (targetType === "decimal" && observation.detectedFormat === "numericString") return ["double-from-string-or-number"];
  if ((targetType === "url" || targetType === "photo") && observation.detectedFormat === "url") return ["url-string"];
  if (targetType === "text" && observation.observedTypes.includes("string")) return ["trim"];
  return ["identity"];
}

function transformedValue(rawValue: unknown, mapping: ExternalFieldMapping): LeagueDynamicFieldValue | null | string {
  let current: unknown = rawValue;
  const transforms: ExternalValueTransform[] = mapping.transforms.length ? mapping.transforms : ["identity"];
  for (const transform of transforms) {
    const next = applyTransform(transform, current, mapping);
    if (typeof next === "string" && next.startsWith("error:")) return next.slice(6);
    if (next === null) return null;
    current = next;
  }
  const decoded = decodeDynamicValue(current);
  return decoded ?? "The value could not be converted to a supported type.";
}

function applyTransform(transform: ExternalValueTransform, value: unknown, mapping: ExternalFieldMapping): unknown | null | string {
  switch (transform) {
    case "identity":
      return value;
    case "string":
      return typeof value === "string" ? value : typeof value === "number" || typeof value === "boolean" ? String(value) : "error:The value could not be converted to text.";
    case "trim": {
      if (typeof value !== "string") return value;
      const trimmed = value.trim();
      return trimmed ? trimmed : null;
    }
    case "date-time-to-date": {
      const date = value instanceof Date ? value : typeof value === "string" ? new Date(value) : undefined;
      return date && !Number.isNaN(date.getTime()) ? date : "error:The value is not an ISO 8601 date-time.";
    }
    case "date-from-string": {
      if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value.trim())) return "error:The value is not a YYYY-MM-DD date.";
      const date = new Date(`${value.trim()}T00:00:00Z`);
      return Number.isNaN(date.getTime()) ? "error:The value is not a YYYY-MM-DD date." : date;
    }
    case "string-boolean-to-boolean": {
      if (typeof value === "boolean") return value;
      if (typeof value !== "string") return "error:The value is not boolean-like text.";
      switch (value.trim().toLowerCase()) {
        case "true":
        case "yes":
        case "1":
          return true;
        case "false":
        case "no":
        case "0":
          return false;
        default:
          return `error:The value '${value}' is not boolean-like text.`;
      }
    }
    case "int-from-string-or-number": {
      const number = typeof value === "number" ? value : typeof value === "string" ? Number(value.trim()) : Number.NaN;
      return Number.isFinite(number) ? Math.trunc(number) : "error:The value could not be converted to an integer.";
    }
    case "double-from-string-or-number": {
      const number = typeof value === "number" ? value : typeof value === "string" ? Number(value.trim()) : Number.NaN;
      return Number.isFinite(number) ? number : "error:The value could not be converted to a decimal.";
    }
    case "value-map": {
      const source = stringRepresentation(value);
      const mapped = source ? mappedValue(source, mapping.valueMap) : undefined;
      return mapped ?? "error:The value could not be matched against the value map.";
    }
    case "url-string": {
      if (typeof value !== "string") return "error:The value is not a URL string.";
      try {
        const url = new URL(value.trim());
        return url.protocol === "http:" || url.protocol === "https:" ? url.toString() : "error:The value is not an http(s) URL.";
      } catch {
        return "error:The value is not a URL string.";
      }
    }
    case "hex-color": {
      if (typeof value !== "string") return "error:The value is not a hex color.";
      const body = value.trim().replace(/^#/, "");
      return /^[0-9a-fA-F]{6}$/.test(body) ? `#${body.toUpperCase()}` : "error:The value is not a hex color.";
    }
  }
}

function resolvedValue(mapping: ExternalFieldMapping, record: Record<string, unknown>): unknown {
  for (const path of [mapping.sourcePath, ...mapping.fallbackPaths]) {
    const value = valueAtPath(record, path);
    if (value === undefined || value === null) continue;
    if (typeof value === "string" && mapping.sentinelValues.includes(value.trim())) continue;
    return value;
  }
  return undefined;
}

function valueAtPath(record: Record<string, unknown>, path: string): unknown {
  let current: unknown = record;
  for (const component of pathComponents(path)) {
    if (component.kind === "key") {
      const currentRecord = asRecord(current);
      if (!currentRecord) return undefined;
      current = currentRecord[component.key];
    } else {
      if (!Array.isArray(current) || component.index < 0 || component.index >= current.length) return undefined;
      current = current[component.index];
    }
  }
  return current;
}

function pathComponents(path: string): Array<{ kind: "key"; key: string } | { kind: "index"; index: number }> {
  return path.split(".").flatMap((segment) => {
    const components: Array<{ kind: "key"; key: string } | { kind: "index"; index: number }> = [];
    let keyPart = "";
    let indexPart: string | null = null;
    const flushKey = () => {
      if (keyPart) components.push({ kind: "key", key: keyPart });
      keyPart = "";
    };
    for (const character of segment) {
      if (character === "[") {
        flushKey();
        indexPart = "";
      } else if (character === "]") {
        const index = Number.parseInt(indexPart ?? "", 10);
        if (Number.isInteger(index)) components.push({ kind: "index", index });
        indexPart = null;
      } else if (indexPart !== null) indexPart += character;
      else keyPart += character;
    }
    flushKey();
    return components;
  });
}

function remapByResolvedFieldID(
  values: Record<string, LeagueDynamicFieldValue>,
  profile: ExternalFieldMappingProfile,
  fieldIDByRole: Map<LeagueAthleteUniversalFieldRole, string>,
): Record<string, LeagueDynamicFieldValue> {
  const resolved = new Map<string, string>();
  for (const mapping of profile.mappings) {
    if (mapping.targetUniversalRole) {
      const fieldID = fieldIDByRole.get(mapping.targetUniversalRole);
      if (fieldID) resolved.set(mapping.targetFieldID, fieldID);
    }
  }
  return Object.fromEntries(Object.entries(values).map(([key, value]) => [resolved.get(key) ?? key, value]));
}

function uniqueImportID(
  leagueID: string,
  profile: ExternalFieldMappingProfile,
  allValues: Record<string, LeagueDynamicFieldValue>,
  fieldIDByRole: Map<LeagueAthleteUniversalFieldRole, string>,
  participantID: string | undefined,
  taken: Set<string>,
): string {
  const seed = idSeedString(profile, allValues, fieldIDByRole, participantID);
  if (!seed) return athleteValueID(leagueID);
  const base = athleteValueID(leagueID, seed);
  if (!taken.has(base)) return base;
  const suffixed = athleteValueID(leagueID, `${seed}-${hashSuffix(seed)}`);
  return taken.has(suffixed) ? athleteValueID(leagueID) : suffixed;
}

function candidateID(
  leagueID: string,
  profile: ExternalFieldMappingProfile,
  allValues: Record<string, LeagueDynamicFieldValue>,
  fieldIDByRole: Map<LeagueAthleteUniversalFieldRole, string>,
  participantID: string | undefined,
): string {
  return athleteValueID(leagueID, idSeedString(profile, allValues, fieldIDByRole, participantID) ?? "");
}

function idSeedString(
  profile: ExternalFieldMappingProfile,
  allValues: Record<string, LeagueDynamicFieldValue>,
  fieldIDByRole: Map<LeagueAthleteUniversalFieldRole, string>,
  participantID: string | undefined,
): string | undefined {
  if (profile.recordKeyStrategy === "participant-id" && participantID && sbotSlug(participantID)) return participantID;
  if (profile.recordKeyStrategy !== "random") {
    const fullName = stringValue(allValues[fieldIDByRole.get("full-name") ?? ""]);
    if (fullName && sbotSlug(fullName)) return fullName;
  }
  return undefined;
}

function matchScore(observation: ExternalSourceFieldObservation, role: LeagueAthleteUniversalFieldRole, countries: CountryResource[]): number {
  return Math.min(1, nameScore(observation.keyName, role) + valueShapeScore(observation, role, countries));
}

function nameScore(key: string, role: LeagueAthleteUniversalFieldRole): number {
  const normalized = normalizeKey(key);
  if (roleSynonyms[normalized] === role) return 0.9;
  if (normalizeKey(UNIVERSAL_ATHLETE_ROLE_LABELS[role]) === normalized) return 0.8;
  if (normalized.includes(normalizeKey(UNIVERSAL_ATHLETE_ROLE_LABELS[role]))) return 0.55;
  return 0;
}

function valueShapeScore(observation: ExternalSourceFieldObservation, role: LeagueAthleteUniversalFieldRole, countries: CountryResource[]): number {
  if (role === "country-name" && observation.sampleValues.some((value) => countries.some((country) => country.officialNameEN.toLowerCase() === value.toLowerCase()))) return 0.35;
  if ((role === "flag-url" || role === "headshot-url") && observation.detectedFormat === "url") return 0.3;
  if ((role === "date-of-birth" || role === "last-updated-at") && (observation.detectedFormat === "isoDate" || observation.detectedFormat === "isoDateTime")) return 0.25;
  if ((role === "is-racing-today" || role === "is-notable") && (observation.detectedFormat === "booleanString" || observation.observedTypes.includes("bool"))) return 0.25;
  if ((role === "age" || role === "notable-sort-key") && (observation.observedTypes.includes("integer") || observation.detectedFormat === "numericString")) return 0.2;
  return 0;
}

function detectFormat(value: string): ExternalDetectedFormat | undefined {
  if (["true", "false", "yes", "no"].includes(value.toLowerCase())) return "booleanString";
  if (/^#?[0-9a-fA-F]{6}$/.test(value) && (value.startsWith("#") || /[a-fA-F]/.test(value))) return "hexColor";
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return "isoDate";
  if (value.includes("T") && !Number.isNaN(new Date(value).getTime())) return "isoDateTime";
  if (/^https?:\/\//i.test(value)) {
    try {
      return new URL(value).host ? "url" : undefined;
    } catch {
      return undefined;
    }
  }
  if (value.length > 0 && Number.isFinite(Number(value))) return "numericString";
  return undefined;
}

function isMappable(observation: ExternalSourceFieldObservation): boolean {
  return observation.observedTypes.some((type) => type === "string" || type === "integer" || type === "double" || type === "bool");
}

function fallbackPathsFor(observation: ExternalSourceFieldObservation): string[] {
  return observation.mapKeyedGroup?.memberKeys.filter((key) => key !== observation.keyName) ?? [];
}

function markDuplicateConcepts(observations: ExternalSourceFieldObservation[]) {
  const byNormalized = new Map<string, ExternalSourceFieldObservation[]>();
  observations.forEach((observation) => {
    const normalized = normalizeKey(observation.keyName.replace(/\d{4}/g, ""));
    const entries = byNormalized.get(normalized) ?? [];
    entries.push(observation);
    byNormalized.set(normalized, entries);
  });
  for (const [normalizedPattern, entries] of byNormalized) {
    if (entries.length < 2) continue;
    const primary = [...entries].sort((first, second) => second.keyName.localeCompare(first.keyName))[0];
    entries.forEach((entry) => {
      entry.duplicateConceptGroup = normalizedPattern;
      entry.mapKeyedGroup = { normalizedPattern, memberKeys: entries.map((item) => item.path), primaryKey: primary.path };
    });
  }
}

function parseNewField(value: unknown, observation: ExternalSourceFieldObservation, sortOrder: number): LeagueAthleteFieldDefinition {
  const data = asRecord(value);
  const rawID = typeof data?.id === "string" ? data.id : observation.keyName;
  const label = typeof data?.label === "string" ? data.label : titleLabel(observation.keyName);
  const type = typeof data?.type === "string" && FIELD_TYPES.includes(data.type as LeagueAthleteFieldType) ? (data.type as LeagueAthleteFieldType) : inferredFieldType(observation);
  return { ...newFieldFromObservation(observation, sortOrder), id: sbotSlug(rawID), label, type };
}

function parseRole(value: unknown): LeagueAthleteUniversalFieldRole | undefined {
  return typeof value === "string" && UNIVERSAL_ATHLETE_FIELD_ROLES.includes(value as LeagueAthleteUniversalFieldRole)
    ? (value as LeagueAthleteUniversalFieldRole)
    : undefined;
}

function parseTransforms(value: unknown): ExternalValueTransform[] {
  return stringArray(value).filter((entry): entry is ExternalValueTransform => EXTERNAL_VALUE_TRANSFORMS.includes(entry as ExternalValueTransform));
}

function parseValueMap(value: unknown): ExternalValueMapEntry[] {
  if (Array.isArray(value)) {
    return value.flatMap((entry) => {
      const data = asRecord(entry);
      return typeof data?.from === "string" && typeof data.to === "string" ? [{ from: data.from, to: data.to }] : [];
    });
  }
  const data = asRecord(value);
  return data ? Object.entries(data).flatMap(([from, to]) => (typeof to === "string" ? [{ from, to }] : [])) : [];
}

function fieldIDsByRole(fields: LeagueAthleteFieldDefinition[]): Map<LeagueAthleteUniversalFieldRole, string> {
  const result = new Map<LeagueAthleteUniversalFieldRole, string>();
  fields.forEach((field) => {
    if (field.universalAthleteRole && !result.has(field.universalAthleteRole)) result.set(field.universalAthleteRole, field.id);
  });
  return result;
}

function keyName(path: string): string {
  const last = path.split(".").at(-1) ?? path;
  return last.replace(/\[\d+\]/g, "");
}

function normalizeKey(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, "");
}

function sbotSlug(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
    .join("-");
}

function titleLabel(value: string): string {
  return value
    .replace(/[-_]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .split(" ")
    .filter(Boolean)
    .map((word) => word[0].toUpperCase() + word.slice(1))
    .join(" ");
}

function stringValue(value: LeagueDynamicFieldValue | undefined): string | undefined {
  return value?.kind === "string" ? value.value.trim() || undefined : undefined;
}

function stringRepresentation(value: unknown): string | undefined {
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return undefined;
}

function mappedValue(value: string, valueMap: ExternalValueMapEntry[]): string | undefined {
  const trimmed = value.trim();
  return valueMap.find((entry) => entry.from === trimmed)?.to ?? valueMap.find((entry) => entry.from.toLowerCase() === trimmed.toLowerCase())?.to;
}

function dynamicValueText(value: LeagueDynamicFieldValue): string {
  return dynamicValueDisplayText(value).replace(/^—$/, "");
}

export function mappedRecordSearchText(mapped: ExternalMappedRecord): string {
  return Object.values(mapped.values).map(dynamicValueText).join(" ").toLowerCase();
}

function hashSuffix(seed: string): string {
  let hash = 2166136261;
  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(16).padStart(8, "0").slice(0, 8);
}

function isSentinelString(value: string): boolean {
  return DEFAULT_SENTINEL_VALUES.includes(value.trim());
}

function clamp(value: number): number {
  return Math.min(Math.max(value, 0), 1);
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === "string") : [];
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return typeof value === "object" && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : undefined;
}

export function emptyScopedMaps(): ScopedDynamicValueMaps {
  return { publicValues: {}, organizationValues: {}, leagueValues: {} };
}
